import React, { useState } from 'react';
import css from './DialectPositionSection.module.css';
import Field, { hasDataInFields } from '../../containers/PageBuilder/Field';

// Pulls the raw markdown/text string out of a Field-shaped data object or string
const getRawContent = field => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field.content || field.markdown || field.text || '';
};

// Extracts string content from title field safely
const getTitleContent = titleField => {
    if (!titleField) return '';
    if (typeof titleField === 'string') return titleField;
    return titleField.content || titleField.text || '';
};

// Parses block text into { language, why } supporting markdown labels:
// **Language:** Your family's dialect...
// **Why:** This is the language of home...
const parseDialectText = field => {
    const raw = getRawContent(field).replace(/\r\n/g, '\n').trim();
    if (!raw) return { language: '', why: '' };

    // Match "Language" pattern - handles **Language:**, **Language**: , Language:, etc.
    const langMatch = raw.match(
        /(?:\*{1,2})?Language:?(?:\*{1,2})?:?\s*([\s\S]*?)(?=(?:\*{1,2})?Why:?|$)/i
    );

    // Match "Why" pattern - handles **Why:**, **Why**: , Why:, etc.
    const whyMatch = raw.match(
        /(?:\*{1,2})?Why:?(?:\*{1,2})?:?\s*([\s\S]*?)$/i
    );

    let language = langMatch ? langMatch[1].trim() : '';
    let why = whyMatch ? whyMatch[1].trim() : '';

    // Clean any trailing bold markdown markers
    language = language.replace(/^\*\*|\*\*$/g, '').trim();
    why = why.replace(/^\*\*|\*\*$/g, '').trim();

    // Fallback if no explicit labels are found
    if (!language && !why) {
        const parts = raw.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
        if (parts.length >= 2) {
            language = parts[0];
            why = parts[1];
        } else if (parts.length === 1) {
            language = parts[0];
        }
    }

    return { language, why };
};

const DialectPositionSection = ({ blocks = [], options, title, sectionId }) => {
    const fieldComponents = options?.fieldComponents;
    const fieldOptions = { fieldComponents };

    const [expanded, setExpanded] = useState(0); // first row open by default on mobile

    const hasTitle = hasDataInFields([title], fieldOptions);

    // Specific blocks by name / blockId
    const introLeftBlock = blocks.find(
        b => b?.blockId === 'dialect-intro-left' || b?.blockId?.includes('intro-left')
    );
    const introRightBlock = blocks.find(
        b => b?.blockId === 'dialect-intro-right' || b?.blockId?.includes('intro-right')
    );
    const positionLabelBlock = blocks.find(
        b => b?.blockId === 'dialect-position-label' || b?.blockId?.includes('position-label')
    );
    const footerBlock = blocks.find(
        b => b?.blockId === 'dialect-footer' || b?.blockId?.includes('footer')
    );

    // Table rows
    let rowBlocks = blocks.filter(
        b => b?.blockId?.startsWith('dialect-row-') || b?.blockId?.startsWith('row-')
    );
    if (rowBlocks.length === 0) {
        rowBlocks = blocks.filter(
            b =>
                b !== introLeftBlock &&
                b !== introRightBlock &&
                b !== positionLabelBlock &&
                b !== footerBlock
        );
    }

    const hasIntroLeft = hasDataInFields([introLeftBlock?.text], fieldOptions);
    const hasIntroRight = hasDataInFields([introRightBlock?.text], fieldOptions);
    const hasPositionLabel = hasDataInFields([positionLabelBlock?.text], fieldOptions);
    const hasFooterText = hasDataInFields([footerBlock?.text], fieldOptions);

    const toggle = index => {
        setExpanded(prev => (prev === index ? -1 : index));
    };

    return (
        <section id={sectionId || 'dialect-position-section'} className={css.dialectSection}>
            {/* ---------- Centered Section Title ---------- */}
            {hasTitle ? (
                <div className={css.titleWrapper}>
                    <Field data={title} className={css.title} options={fieldOptions} />
                </div>
            ) : null}

            {/* ---------- Two Column Intro Text ---------- */}
            {hasIntroLeft || hasIntroRight ? (
                <div className={css.introGrid}>
                    <div className={css.introLeft}>
                        {hasIntroLeft ? (
                            <Field data={introLeftBlock.text} className={css.introText} options={fieldOptions} />
                        ) : null}
                    </div>
                    <div className={css.introRight}>
                        {hasIntroRight ? (
                            <Field data={introRightBlock.text} className={css.introText} options={fieldOptions} />
                        ) : null}
                    </div>
                </div>
            ) : null}

            {/* ---------- "Arabibi takes a position:" Label ---------- */}
            {hasPositionLabel ? (
                <div className={css.positionLabelWrapper}>
                    <Field
                        data={positionLabelBlock.text}
                        className={css.positionLabel}
                        options={fieldOptions}
                    />
                </div>
            ) : null}

            {/* ---------- Desktop Table ---------- */}
            <div className={css.tableWrapper}>
                <div className={css.tableHeader}>
                    <span className={css.colStrand}>Strand</span>
                    <span className={css.colLanguage}>Language</span>
                    <span className={css.colWhy}>Why</span>
                </div>

                {rowBlocks.map((block, index) => {
                    const hasBlockTitle = hasDataInFields([block?.title], fieldOptions);
                    const { language, why } = parseDialectText(block?.text);
                    const titleText = getTitleContent(block?.title);
                    const isAltRow = index % 2 === 1;

                    if (!hasBlockTitle && !titleText) return null;

                    return (
                        <div
                            key={block.blockId || `dialect-row-${index}`}
                            className={`${css.tableRow} ${isAltRow ? css.tableRowAlt : ''}`}
                        >
                            <div className={css.colStrand}>
                                <span className={css.strandName}>
                                    {titleText || <Field data={block.title} options={fieldOptions} />}
                                </span>
                            </div>
                            <div className={css.colLanguage}>
                                <span className={css.cellTextLanguage}>{language}</span>
                            </div>
                            <div className={css.colWhy}>
                                <span className={css.cellTextWhy}>{why}</span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* ---------- Mobile Accordion ---------- */}
            <div className={css.accordionWrapper}>
                <div className={css.accordionContainer}>
                    {rowBlocks.map((block, index) => {
                        const hasBlockTitle = hasDataInFields([block?.title], fieldOptions);
                        const { language, why } = parseDialectText(block?.text);
                        const titleText = getTitleContent(block?.title);
                        const isOpen = expanded === index;

                        if (!hasBlockTitle && !titleText) return null;

                        return (
                            <div
                                key={block.blockId || `acc-dialect-${index}`}
                                className={`${css.accordionItem} ${isOpen ? css.accordionItemOpen : ''}`}
                            >
                                <button
                                    type="button"
                                    className={css.accordionHeader}
                                    onClick={() => toggle(index)}
                                    aria-expanded={isOpen}
                                >
                                    <span className={css.accordionLabel}>
                                        <span className={css.accordionTitleText}>
                                            {titleText || <Field data={block.title} options={fieldOptions} />}
                                        </span>
                                    </span>
                                    <svg
                                        className={`${css.chevron} ${isOpen ? css.chevronOpen : ''}`}
                                        width="18"
                                        height="18"
                                        viewBox="0 0 20 20"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <polyline points="6 8 10 12 14 8" />
                                    </svg>
                                </button>

                                {isOpen ? (
                                    <div className={css.accordionBody}>
                                        <div className={css.accordionFieldGroup}>
                                            <p className={css.accordionFieldLabel}>Language</p>
                                            <p className={css.accordionFieldValue}>{language}</p>
                                        </div>

                                        <div className={css.accordionFieldGroup}>
                                            <p className={css.accordionFieldLabel}>Why</p>
                                            <p className={css.accordionFieldValue}>{why}</p>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ---------- Footer Paragraph ---------- */}
            {hasFooterText ? (
                <div className={css.footerTextWrapper}>
                    <Field data={footerBlock.text} className={css.footerText} options={fieldOptions} />
                </div>
            ) : null}
        </section>
    );
};

export default DialectPositionSection;
